import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowUpRight, ArrowLeft, ArrowRight } from "lucide-react";
import { readCache, fetchWithCache } from "@/lib/swrCache";
// Per-industry accent (used for the hover duotone tint)
const INDUSTRY_TINT = {
  "Real Estate":         "rgba(168, 85, 247, 0.55)",
  "Financial Services":  "rgba(56, 189, 248, 0.55)",
  "Logistics":           "rgba(244, 114, 182, 0.55)",
  "Healthcare":          "rgba(239, 68, 68, 0.5)",
  "Education":           "rgba(16, 185, 129, 0.55)",
  "Hospitality":         "rgba(251, 191, 36, 0.55)",
  "Manufacturing":       "rgba(59, 130, 246, 0.55)",
};

// Dark base — the fade masks blend into this color at the corners
const BASE_HSL = "hsl(215 35% 8%)";

export default function FeaturedProjects() {
  // Hydrate synchronously from sessionStorage so returning visitors see the
  // cards on the very first paint — no waiting for the API round-trip.
  const [projects, setProjects] = useState(() => readCache("/projects")?.data || []);
  const scrollerRef = useRef(null);

  useEffect(() => {
    // Stale-while-revalidate: fetch in the background and swap in fresh data
    // silently. If the cache was empty, this is the initial fetch.
    fetchWithCache("/projects", (fresh) => setProjects(fresh)).catch(() => {});
  }, []);

  // Manual scrolling only — no auto-play

  const nudge = (dir) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * 272, behavior: "smooth" });
  };

  // Skeleton placeholders — keep the section visible during the very first
  // page-load fetch so users never see a blank stretch where cards will land.
  const isLoading = projects.length === 0;

  return (
    <section
      className="relative py-8 md:py-12 overflow-hidden"
      data-testid="featured-projects-section"
      style={{
        background:
          // Palette lifted from the mountain-meadow reference: deep slate + olive-gold + poppy-orange + red
          "radial-gradient(50% 70% at 15% 20%, hsl(48 65% 45%) 0%, transparent 55%), " +   // mustard meadow top-left
          "radial-gradient(45% 65% at 45% 12%, hsl(210 30% 22%) 0%, transparent 55%), " +  // cool slate top-mid
          "radial-gradient(55% 75% at 82% 30%, hsl(22 90% 55%) 0%, transparent 55%), " +   // marigold right
          "radial-gradient(50% 70% at 95% 78%, hsl(8 75% 45%) 0%, transparent 55%), " +    // poppy red bottom-right
          "radial-gradient(45% 65% at 22% 85%, hsl(85 40% 35%) 0%, transparent 55%), " +   // olive green bottom-left
          "linear-gradient(180deg, hsl(215 35% 12%) 0%, hsl(215 35% 6%) 100%)",             // deep mountain base
      }}
    >
      {/* Subtle grain overlay to soften the gradient banding */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")",
        }}
      />

      {/* Header — long descriptive title with a big outlined stat + arrow controls on the right, ALL on one row */}
      <div className="relative max-w-[1400px] mx-auto px-6 mb-6 md:mb-8">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div className="max-w-3xl">
            <div className="text-xs font-mono uppercase tracking-[0.28em] text-white/60">
              Case studies
            </div>
            <h2
              className="mt-4 font-heading font-bold text-white text-3xl md:text-4xl lg:text-[44px] tracking-tight leading-[1.1]"
              data-testid="featured-projects-title"
            >
              Exploring groundbreaking AI projects transforming industries and driving innovation.
            </h2>
          </div>

          <div className="flex flex-col items-end gap-4 shrink-0">
            {/* Outlined stat block */}
            <div className="flex items-stretch gap-3">
              <div
                className="font-heading font-black italic leading-none tracking-tight text-transparent select-none"
                style={{
                  WebkitTextStroke: "1.5px rgba(255,255,255,0.35)",
                  fontSize: "clamp(5rem, 11vw, 9rem)",
                }}
                aria-label="200 plus projects"
              >
                200+
              </div>
              <div
                className="text-xs font-mono uppercase tracking-[0.3em] text-white/50 self-end pb-2"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                Projects
              </div>
            </div>

            {/* Prev / next arrows */}
            <div className="flex gap-3 items-center">
              <Link
                to="/portfolio"
                data-testid="featured-projects-view-more"
                className="rounded-full px-4 py-2 border border-white/25 text-white text-xs font-mono uppercase tracking-widest hover:bg-white/15 transition-colors backdrop-blur-sm inline-flex items-center gap-1.5"
              >
                View full portfolio <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <button
                onClick={() => nudge(-1)}
                aria-label="Previous projects"
                data-testid="projects-prev"
                className="w-11 h-11 rounded-full border border-white/25 text-white hover:bg-white/15 transition-colors flex items-center justify-center backdrop-blur-sm"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => nudge(1)}
                aria-label="Next projects"
                data-testid="projects-next"
                className="w-11 h-11 rounded-full border border-white/25 text-white hover:bg-white/15 transition-colors flex items-center justify-center backdrop-blur-sm"
              >
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable strip. Arrow buttons drive navigation manually. */}
      <div className="relative max-w-[1400px] mx-auto">
        {/* Stronger corner fade — cards visibly fade in/out at both edges */}
        <div
          className="pointer-events-none absolute inset-y-0 left-0 w-32 md:w-40 z-10"
          style={{ background: `linear-gradient(90deg, ${BASE_HSL} 0%, ${BASE_HSL.replace(")", " / 0.6)")} 50%, transparent 100%)` }}
        />
        <div
          className="pointer-events-none absolute inset-y-0 right-0 w-32 md:w-40 z-10"
          style={{ background: `linear-gradient(270deg, ${BASE_HSL} 0%, ${BASE_HSL.replace(")", " / 0.6)")} 50%, transparent 100%)` }}
        />

        <div
          ref={scrollerRef}
          className="flex gap-5 overflow-x-auto no-scrollbar px-6 scroll-smooth"
          data-testid="featured-projects-marquee"
          style={{ scrollBehavior: "auto" }}
        >
          {isLoading && Array.from({ length: 5 }).map((_, i) => (
            <div
              key={`sk-${i}`}
              aria-hidden="true"
              className="shrink-0 w-[240px] md:w-[252px] h-[400px] md:h-[420px] rounded-3xl overflow-hidden bg-white/[0.04] border border-white/10 animate-pulse"
            />
          ))}
          {projects.map((p, i) => {
            const tint = INDUSTRY_TINT[p.industry] || "rgba(139, 92, 246, 0.55)";
            return (
              <Link
                key={p.id}
                to={
                  p.publish_as_case_study && p.publication_status === "PUBLISHED"
                    ? `/case-studies/${p.slug}`
                    : p.linked_portfolio_slug
                      ? `/portfolio/${p.linked_portfolio_slug}`
                      : `/projects/${p.slug}`
                }
                state={{ project: p }}
                data-testid={`project-card-${p.slug}`}
                className="group/card relative shrink-0 w-[240px] md:w-[252px] h-[400px] md:h-[420px] rounded-3xl overflow-hidden shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)]"
                aria-label={p.name}
              >
                <img
                  src={p.thumbnail_image || p.cover_image}
                  alt={p.name}
                  loading={i === 0 ? "eager" : "lazy"}
                  fetchpriority={i === 0 ? "high" : "auto"}
                  decoding="async"
                  draggable={false}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover/card:scale-[1.06]"
                />

                <div
                  aria-hidden="true"
                  className="absolute inset-0 mix-blend-multiply opacity-0 group-hover/card:opacity-100 transition-opacity duration-500"
                  style={{ background: tint }}
                />

                <div
                  aria-hidden="true"
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, transparent 30%, rgba(0,0,0,0.15) 55%, rgba(0,0,0,0.75) 100%)",
                  }}
                />

                <div className="absolute top-4 right-4 z-10 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur-md text-[10px] font-mono uppercase tracking-[0.2em] text-white/90 border border-white/20">
                  {p.industry}
                </div>

                <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 z-10 text-white">
                  <div className="inline-flex items-center gap-1.5 text-[11px] text-white/70 opacity-0 group-hover/card:opacity-100 transition-opacity duration-300 delay-75">
                    <MapPin className="w-3 h-3" />
                    {p.country}
                  </div>
                  <h3 className="mt-1 font-heading font-bold text-xl md:text-2xl leading-tight">
                    {p.name}
                  </h3>
                  <p className="mt-2 text-sm text-white/80 leading-relaxed max-h-0 opacity-0 group-hover/card:max-h-40 group-hover/card:opacity-100 group-hover/card:mt-3 transition-[max-height,opacity,margin] duration-500 line-clamp-4">
                    {p.tagline}
                  </p>
                  <div className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-white/90 opacity-0 group-hover/card:opacity-100 transition-opacity duration-500 delay-100">
                    Read case study <ArrowUpRight className="w-3.5 h-3.5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 mt-10 flex items-center justify-between text-white/50">
        <p className="text-xs max-w-md">
          * Every engagement is tailored to your data, compliance and integration requirements.
        </p>
        <Link
          to="/quote"
          className="text-xs font-medium text-white/80 hover:text-white inline-flex items-center gap-1 transition-colors"
          data-testid="featured-projects-cta"
        >
          Start your project <ArrowUpRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    </section>
  );
}
